import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import colors from "@constants/colors"
import animations from "@constants/animations"
import { Box, Text } from "@chakra-ui/react"
import Backdrop from "@components/Backdrop"
import MenuItemCheckboxGroup from "@components/MenuItemCheckboxGroup"
import MenuItemRadioGroup from "@components/MenuItemRadioGroup"
import RangeSlider from "@components/form/inputs/RangeSlider"
import { toggleKeyInArray } from "@utils/index"
import {
  ProductView,
  SortMethods,
  FilterType,
  productSliderFilters,
} from "../../common"
import _ from "lodash"
import { Close } from "@components/universal/icons"

const openProps = css`
  opacity: 1;
  transform: translateX(0);
  transition: opacity 100ms ${animations.bezier},
    transform 250ms ${animations.bezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  ${({ isOpen }) => css`
    z-index: 2000;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    flex-direction: column;
    display: flex;
    opacity: 0;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateX(100%);
    transition: opacity 250ms ${animations.bezier},
      transform 100ms ${animations.bezier};
    will-change: opacity, transform;
    width: 100%;
    background-color: ${colors.white};
    cursor: default;

    @media only screen and (min-width: 1024px) {
      display: none;
    }

    ${isOpen ? openProps : ""}
  `}
`

export interface FilterSlideroutProps {
  isOpen: boolean
  view: ProductView
  sort: SortMethods
  path: Array<string>
  lastClear?: number
  filters?: any
  availableFilters?: any
  activeFilters?: any
  onFilter?: (filters: any) => void
  onClearFilter?: () => void
  setView: (view) => void
  setSort: (sort) => void
  onClose: () => void
}

const renderCheckbox = (path, key, items) => {
  return (
    <MenuItemCheckboxGroup
      key={`${path.join(", ")}-${key}`}
      label={key}
      items={items}
    />
  )
}

const renderSlider = (lastClear, path, key, label, min, max, onChange) => {
  return (
    <Box
      key={`${lastClear}-${path.join(", ")}-${key}`}
      w="100%"
      h="136px"
      p="20px 0"
      borderBottom="solid 1px #E2E8F0"
    >
      <Text
        mb="24px"
        fontFamily="Avenir"
        fontSize="16px"
        fontWeight="800"
        lineHeight="20px"
        color="dark"
      >
        {label}
      </Text>
      <RangeSlider
        min={min}
        max={max}
        onChange={onChange}
        decimalPlaces={label === "Rack Units" ? 0 : 2}
        isCurrency={label === "Filter by Price"}
      />
    </Box>
  )
}

export const FilterSliderout: React.FC<FilterSlideroutProps> = ({
  isOpen,
  view,
  sort,
  path,
  lastClear,
  filters,
  availableFilters,
  activeFilters,
  onFilter,
  onClearFilter,
  setView,
  setSort,
  onClose,
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState<any>([])

  React.useEffect(() => {
    setSelectedFilters(activeFilters.filters)
  }, [path, filters, activeFilters])

  const formattedFilters = React.useMemo(() => {
    const data = {}

    if (filters && Object.keys(filters).length > 0) {
      const filtersToFormat = Object.keys(filters).filter(
        (k) => filters[k] && (k === "Series" || filters[k].length > 1)
      )

      filtersToFormat.forEach((key) => {
        const dataProps = {} as any

        if (productSliderFilters.indexOf(key) !== -1) {
          dataProps.type = FilterType.SLIDER
          dataProps.label = key

          const range = filters[key].map((f) => parseFloat(f))
          dataProps.min = _.min(range)
          dataProps.max = _.max(range)
          dataProps.onChange = (values) => {
            const rangeValue = dataProps.max - dataProps.min
            const [min, max] = values
            let adjustedMin, adjustedMax

            if (min === 0) {
              adjustedMin = dataProps.min
            } else {
              adjustedMin = (min / 100) * rangeValue + dataProps.min
            }

            if (max === 100) {
              adjustedMax = dataProps.max
            } else {
              adjustedMax = (max / 100) * rangeValue + dataProps.min
            }

            const updatedFilters = {
              ...selectedFilters,
              [key]: {
                min: adjustedMin,
                max: adjustedMax,
                pristine:
                  adjustedMin === dataProps.min &&
                  adjustedMax === dataProps.max,
              },
            }

            setSelectedFilters(updatedFilters)

            if (onFilter) {
              onFilter(updatedFilters)
            }
          }
        } else {
          dataProps.type = FilterType.CHECKBOX
          dataProps.label = key
          dataProps.items = filters[key].map((f) => ({
            label: f,
            checked:
              selectedFilters[key] &&
              selectedFilters[key].values &&
              selectedFilters[key].values.indexOf(f) !== -1,
            disabled:
              availableFilters &&
              ((availableFilters[key] &&
                availableFilters[key].indexOf(f) === -1) ||
                !availableFilters[key]),
            onChange: () => {
              const updatedFilters = {
                ...selectedFilters,
                [key]: {
                  values: toggleKeyInArray(
                    f,
                    selectedFilters[key]?.values || []
                  ),
                },
              }

              setSelectedFilters(updatedFilters)

              if (onFilter) {
                onFilter(updatedFilters)
              }
            },
          }))
        }

        data[key] = dataProps
      })
    }

    return data
  }, [path, filters, availableFilters, activeFilters, selectedFilters])

  const FilterElements = React.useMemo(() => {
    return (
      (Object.keys(formattedFilters).length > 0 &&
        Object.keys(formattedFilters).map((key) =>
          formattedFilters[key].type === FilterType.SLIDER
            ? renderSlider(
                lastClear,
                path,
                key,
                formattedFilters[key].label,
                formattedFilters[key].min,
                formattedFilters[key].max,
                formattedFilters[key].onChange
              )
            : renderCheckbox(path, key, formattedFilters[key].items)
        )) ||
      []
    )
  }, [path, formattedFilters])

  const filtersToApply = Object.keys(activeFilters.filters).filter(
    (key) =>
      activeFilters.filters[key] &&
      ((!!activeFilters.filters[key].values &&
        activeFilters.filters[key].values!.length > 0) ||
        (activeFilters.filters[key].min && activeFilters.filters[key]?.max))
  )

  return (
    <>
      <Backdrop isOpen={isOpen} />
      <Container isOpen={isOpen}>
        <Box width="100%">
          <Box
            zIndex="100"
            position={["-webkit-sticky", "sticky"]}
            top={"0"}
            bg="white"
            left={"0"}
            width="100%"
            height="45px"
            display="grid"
            onClick={onClose}
            hidden={!isOpen}
            boxShadow="0 0 1px black"
          >
            <Box
              position="relative"
              alignSelf="center"
              justifySelf="center"
              width="3.8em"
            >
              {"Close"}
              <Box position="absolute" top="8px" right="1" width="11px">
                <Close strokeWidth={"2.5px"} strokeColor="#000" opacity="1" />
              </Box>
            </Box>
          </Box>
          <Box padding="0 20px">
            <MenuItemRadioGroup
              label="Show as"
              items={Object.keys(ProductView).map((v) => ({
                label: ProductView[v],
                selected: view === ProductView[v],
                onClick: () => {
                  setView(ProductView[v])
                },
              }))}
            />
            <MenuItemRadioGroup
              label="Sort by"
              items={Object.keys(SortMethods).map((s) => ({
                label: SortMethods[s],
                selected: sort === SortMethods[s],
                onClick: () => {
                  setSort(SortMethods[s])
                },
              }))}
            />
            {filtersToApply?.length > 0 && (
              <Box justifyContent="center" display="flex" width="100%">
                <Box
                  as="button"
                  height="36px"
                  m="32px 0 12px"
                  padding="0 24px"
                  bg="black"
                  rounded="lg"
                  fontWeight="800"
                  fontFamily="Avenir"
                  fontSize="13.5px"
                  color="white"
                  _focus={{ boxShadow: "outline", outline: "none" }}
                  _hover={{ bg: colors.blackButtonFocus }}
                  onClick={() => {
                    if (onClearFilter) {
                      onClearFilter()
                    }
                  }}
                >
                  Reset Filters
                </Box>
              </Box>
            )}
            {FilterElements.length > 0 && FilterElements}
          </Box>
        </Box>
      </Container>
    </>
  )
}

export default FilterSliderout
